import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@clerk/clerk-react'
import { Globe } from 'lucide-react'
import { api } from '@/lib/api'
import type { SupportedLanguage } from '@/types'

const LANGUAGES: { code: SupportedLanguage; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'ru', label: 'Русский' },
  { code: 'tr', label: 'Türkçe' },
]

export function LanguageSwitcher() {
  const { i18n } = useTranslation()
  const { isSignedIn, getToken } = useAuth()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const changeLanguage = async (code: SupportedLanguage) => {
    setOpen(false)
    i18n.changeLanguage(code)
    localStorage.setItem('i18nextLng', code)
    if (isSignedIn) {
      try {
        const token = await getToken()
        await api.user.updatePreferences(token ?? '', { preferredLanguage: code })
      } catch {
        // Non-fatal — local change already applied
      }
    }
  }

  const currentCode = i18n.language?.slice(0, 2).toUpperCase() ?? 'EN'

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Change language"
        data-testid="language-switcher-button"
        className="flex items-center gap-1 rounded-md px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
      >
        <Globe className="h-4 w-4" />
        <span className="text-xs font-medium">{currentCode}</span>
      </button>
      {open && (
        <div
          data-testid="language-switcher-dropdown"
          className="absolute left-0 md:left-auto md:right-0 mt-1 w-40 rounded-md border bg-popover shadow-md z-50"
        >
          {LANGUAGES.map(({ code, label }) => (
            <button
              key={code}
              onClick={() => changeLanguage(code)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors first:rounded-t-md last:rounded-b-md ${
                i18n.language === code ? 'font-semibold text-primary' : ''
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
